const inflightRequests = new Map();

const isSessionStorageAvailable = () => {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
};

const buildStorageKey = (namespace, key) => {
  return `${namespace}:${JSON.stringify(key)}`;
};

export const getSessionCacheItem = ({ namespace, key }) => {
  if (!namespace || key == null || !isSessionStorageAvailable()) return null;

  const storageKey = buildStorageKey(namespace, key);
  const rawValue = window.sessionStorage.getItem(storageKey);

  if (!rawValue) return null;

  try {
    const parsedValue = JSON.parse(rawValue);

    if (
      parsedValue?.expiresAt &&
      Number.isFinite(parsedValue.expiresAt) &&
      Date.now() > parsedValue.expiresAt
    ) {
      window.sessionStorage.removeItem(storageKey);
      return null;
    }

    return parsedValue?.value ?? null;
  } catch (err) {
    console.error("Failed to parse session cache item:", err);
    window.sessionStorage.removeItem(storageKey);
    return null;
  }
};

export const setSessionCacheItem = ({ namespace, key, value, ttlMs = null }) => {
  if (!namespace || key == null || !isSessionStorageAvailable()) return;

  const storageKey = buildStorageKey(namespace, key);
  const expiresAt =
    Number.isFinite(ttlMs) && ttlMs > 0 ? Date.now() + ttlMs : null;

  try {
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        value,
        expiresAt,
      })
    );
  } catch (err) {
    console.error("Failed to set session cache item:", err);
  }
};

export const removeSessionCacheItem = ({ namespace, key }) => {
  if (!namespace || key == null || !isSessionStorageAvailable()) return;

  window.sessionStorage.removeItem(buildStorageKey(namespace, key));
};

export const getOrSetSessionCache = async ({
  namespace,
  key,
  ttlMs = null,
  fetcher,
}) => {
  if (!namespace || key == null || typeof fetcher !== "function") return null;

  const cachedValue = getSessionCacheItem({ namespace, key });
  if (cachedValue != null) {
    return cachedValue;
  }

  const inflightKey = buildStorageKey(namespace, key);
  const existingRequest = inflightRequests.get(inflightKey);

  if (existingRequest) {
    return existingRequest;
  }

  const requestPromise = (async () => {
    const fetchedValue = await fetcher();

    if (fetchedValue != null) {
      setSessionCacheItem({
        namespace,
        key,
        value: fetchedValue,
        ttlMs,
      });
    }

    return fetchedValue;
  })();

  inflightRequests.set(inflightKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    inflightRequests.delete(inflightKey);
  }
};
